import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import apiClient from '../utils/api';
import { useAuthStore } from '../store/authStore';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card } from '../components/ui/card';
import { 
  MessageCircle, Send, ArrowLeft, User, Clock, Check, CheckCheck,
  Image, Paperclip, Smile
} from 'lucide-react';
import { toast } from 'sonner';

export default function ChatConversation() {
  const { bookingId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [booking, setBooking] = useState(null);
  const [otherParty, setOtherParty] = useState(null);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const isTeamUser = ['admin', 'owner', 'overseer'].includes(user?.role);

  const scrollToBottom = () => {
    const box = messagesEndRef.current?.parentElement;
    if (box) box.scrollTop = box.scrollHeight;
  };

  // Messages poll (silent); booking + other party load once
  const fetchMessages = useCallback(async (initial = false) => {
    try {
      const messagesRes = await apiClient.get(`/messages/booking/${bookingId}`);
      setMessages(messagesRes.data || []);
      if (initial) {
        const bookingRes = await apiClient.get(`/bookings/${bookingId}`);
        setBooking(bookingRes.data);
        const iAmClient = bookingRes.data.user_id === user?.user_id;
        if (iAmClient || isTeamUser) {
          const artistRes = await apiClient.get(`/artists/${bookingRes.data.artist_id}`).catch(() => null);
          setOtherParty(artistRes?.data ? { name: artistRes.data.stage_name || artistRes.data.bio?.slice(0, 30) || 'Artist' } : { name: 'Artist' });
        } else {
          const clientRes = await apiClient.get(`/users/${bookingRes.data.user_id}`).catch(() => null);
          setOtherParty(clientRes?.data || { name: 'Client' });
        }
      }
    } catch (error) {
      if (initial) toast.error('Failed to load conversation');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line
  }, [bookingId, user?.user_id]);

  useEffect(() => {
    fetchMessages(true);
    // Poll for new messages every 5 seconds (messages only, silent)
    const interval = setInterval(() => fetchMessages(false), 5000);
    return () => clearInterval(interval);
  }, [fetchMessages]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || sending) return;

    setSending(true);
    try {
      await apiClient.post('/messages', {
        booking_id: bookingId,
        content: newMessage.trim()
      });
      setNewMessage('');
      fetchMessages();
      inputRef.current?.focus();
    } catch (error) {
      toast.error('Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const formatTime = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const isToday = date.toDateString() === now.toDateString();
    
    if (isToday) {
      return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
    }
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
  };

  if (loading) {
    return (
      <div className="relative z-10 min-h-screen pt-24 pb-12 px-4 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-violet-500" />
      </div>
    );
  }

  return (
    <div className="relative z-10 min-h-screen pt-20 pb-4 px-4" data-testid="chat-conversation-page">
      <div className="max-w-3xl mx-auto h-[calc(100vh-120px)] flex flex-col">
        {/* Header */}
        <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-t-2xl p-4 flex items-center gap-4">
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={() => navigate(-1)}
            className="text-white/60 hover:text-white"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          
          <div className="w-10 h-10 rounded-full bg-violet-500/20 flex items-center justify-center">
            <User className="w-5 h-5 text-violet-400" />
          </div>
          
          <div className="flex-1">
            <h2 className="text-white font-medium">
              {otherParty?.name || otherParty?.bio?.slice(0, 30) || 'Conversation'}
            </h2>
            <p className="text-white/40 text-sm">
              Booking #{bookingId?.slice(-8)} • {booking?.booking_status || booking?.status || 'inquiry'}
            </p>
            <p className="text-amber-300/70 text-xs">Managed chat — the RazientSounds team can see and reply here</p>
          </div>
        </div>

        {/* Messages Area */}
        <div className="flex-1 overflow-y-auto bg-black/20 backdrop-blur-xl border-x border-white/10 p-4 space-y-4">
          {messages.length === 0 ? (
            <div className="h-full flex flex-col items-center justify-center text-center">
              <MessageCircle className="w-16 h-16 text-white/20 mb-4" />
              <p className="text-white/40">No messages yet</p>
              <p className="text-white/30 text-sm mt-1">Start the conversation!</p>
            </div>
          ) : (
            messages.map((msg, index) => {
              const isOwn = msg.sender_id === user?.user_id;
              const isTeam = ['admin', 'owner', 'overseer'].includes(msg.sender_role);
              const showAvatar = index === 0 || messages[index - 1]?.sender_id !== msg.sender_id;

              if (msg.is_system) {
                return (
                  <div key={msg.message_id || index} className="flex justify-center" data-testid="system-message">
                    <p className="text-xs text-amber-300/90 bg-amber-500/10 border border-amber-500/25 rounded-full px-3 py-1.5 text-center max-w-[85%]">
                      {msg.content}
                    </p>
                  </div>
                );
              }
              
              return (
                <div 
                  key={msg.message_id || index}
                  className={`flex ${isOwn ? 'justify-end' : 'justify-start'}`}
                >
                  <div className={`max-w-[75%] ${isOwn ? 'order-2' : ''}`}>
                    {showAvatar && !isOwn && (
                      <p className="text-white/40 text-xs mb-1 ml-1 flex items-center gap-1.5">
                        {msg.sender_name || 'User'}
                        {isTeam && (
                          <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-amber-500/25 text-amber-300 border border-amber-500/40" data-testid="team-badge">TEAM</span>
                        )}
                      </p>
                    )}
                    <div 
                      className={`rounded-2xl px-4 py-2 ${
                        isOwn 
                          ? 'bg-violet-600 text-white rounded-br-md' 
                          : isTeam
                          ? 'bg-white/10 text-white rounded-bl-md border border-amber-500/40'
                          : 'bg-white/10 text-white rounded-bl-md'
                      }`}
                    >
                      <p className="whitespace-pre-wrap break-words">{msg.content}</p>
                    </div>
                    <div className={`flex items-center gap-1 mt-1 ${isOwn ? 'justify-end' : ''}`}>
                      <span className="text-white/30 text-xs">{formatTime(msg.created_at)}</span>
                      {isOwn && (
                        msg.read ? (
                          <CheckCheck className="w-3 h-3 text-violet-400" />
                        ) : (
                          <Check className="w-3 h-3 text-white/30" />
                        )
                      )}
                    </div>
                  </div>
                </div>
              );
            })
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Input Area */}
        <form 
          onSubmit={sendMessage}
          className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-b-2xl p-4"
        >
          {isTeamUser && (
            <div className="mb-3 flex flex-wrap items-center gap-2" data-testid="team-booking-controls">
              <span className="text-[10px] font-bold uppercase tracking-widest text-amber-400/80 mr-1">Team:</span>
              <Button type="button" size="sm" data-testid="team-confirm-btn" className="bg-emerald-600 hover:bg-emerald-500 h-7 text-xs"
                onClick={async () => { try { await apiClient.post(`/admin/booking-chats/${bookingId}/action`, { action: 'confirm' }); toast.success('✅ Booking confirmed!'); fetchMessages(false); } catch { toast.error('Action failed'); } }}>
                ✅ Confirm
              </Button>
              <Button type="button" size="sm" variant="outline" data-testid="team-cancel-btn" className="border-red-500/50 text-red-300 hover:bg-red-500/20 h-7 text-xs"
                onClick={async () => { if (!window.confirm('Cancel this booking?')) return; try { await apiClient.post(`/admin/booking-chats/${bookingId}/action`, { action: 'cancel' }); toast.success('Booking cancelled'); fetchMessages(false); } catch { toast.error('Action failed'); } }}>
                ✖ Cancel
              </Button>
            </div>
          )}
          <div className="flex items-center gap-3">
            <Input
              ref={inputRef}
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              placeholder="Type a message..."
              className="flex-1 bg-white/5 border-white/10"
              disabled={sending}
              data-testid="chat-input"
            />
            <Button 
              type="submit" 
              disabled={!newMessage.trim() || sending}
              className="bg-violet-600 hover:bg-violet-500"
              data-testid="send-message-btn"
            >
              <Send className="w-4 h-4" />
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
